import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { applyTitleFont, applyAccentColor } from "../../hooks/useSiteTexts";

const fieldCls =
  "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-light text-white placeholder:text-white/40 outline-none transition-colors focus:border-gold";

const TEXT_FIELDS = [
  { key: "hero_title_1", label: "Accroche principale — ligne 1" },
  { key: "hero_title_2", label: "Accroche principale — ligne 2 (affichée en or, italique)" },
  { key: "hero_subtitle", label: "Sous-titre de l'accueil", textarea: true },
  { key: "sell_title", label: "Section « Vendre » — titre" },
  { key: "sell_text", label: "Section « Vendre » — texte", textarea: true },
  { key: "buy_title", label: "Section « Acheter » — titre" },
  { key: "buy_text", label: "Section « Acheter » — texte", textarea: true },
  { key: "whatsapp_message", label: "Message pré-rempli du bouton WhatsApp flottant", textarea: true },
];

const FONT_OPTIONS = [
  "Cormorant Garamond",
  "Playfair Display",
  "Marcellus",
  "EB Garamond",
  "DM Serif Display",
  "Libre Caslon Text",
];

const ACCENT_PRESETS = [
  { name: "Or", value: "#D4AF37" },
  { name: "Champagne", value: "#C9A45F" },
  { name: "Bronze", value: "#B08D57" },
  { name: "Cuivre", value: "#C86D51" },
  { name: "Émeraude", value: "#2F6B5E" },
  { name: "Riviera", value: "#1F4E5F" },
  { name: "Bordeaux", value: "#7A3B45" },
];

export default function TextsManager() {
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/texts").then((r) => setValues(r.data)).catch(() => setValues({}));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/texts", { values });
      toast.success("Textes et style enregistrés — visibles sur le site immédiatement.");
    } catch {
      toast.error("Enregistrement impossible, réessayez.");
    } finally {
      setSaving(false);
    }
  };

  if (!values) return null;

  return (
    <form
      onSubmit={save}
      data-testid="texts-manager"
      className="rounded-2xl border border-white/10 bg-[#1C2541] p-6"
    >
      <h3 className="font-serif text-xl text-white">Textes & style du site</h3>
      <p className="mt-1 text-sm font-light text-white/50">
        Titres de l'accueil, sections Vendre/Acheter, et police des titres sur tout le site.
      </p>

      <div className="mt-5 rounded-xl border border-gold/25 bg-black/20 p-5">
        <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
          Police des titres
        </label>
        <select
          value={values.font_titles || "Cormorant Garamond"}
          onChange={(e) => {
            setValues({ ...values, font_titles: e.target.value });
            applyTitleFont(e.target.value);
          }}
          data-testid="font-titles-select"
          className={fieldCls}
        >
          {FONT_OPTIONS.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
        <p className="mt-4 font-serif text-2xl text-sand" data-testid="font-preview">
          Votre projet immobilier, notre engagement.
        </p>
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/30">
          Aperçu en direct de la police choisie
        </p>
      </div>

      <div className="mt-5 rounded-xl border border-gold/25 bg-black/20 p-5">
        <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
          Couleur d'accent du site
        </label>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {ACCENT_PRESETS.map((c) => (
            <button
              key={c.value}
              type="button"
              onClick={() => {
                setValues({ ...values, accent_color: c.value });
                applyAccentColor(c.value);
              }}
              data-testid={`accent-${c.name.toLowerCase()}`}
              title={c.name}
              className={`h-10 w-10 rounded-full border-2 transition-all duration-200 ${
                (values.accent_color || "#D4AF37").toLowerCase() === c.value.toLowerCase()
                  ? "scale-110 border-white"
                  : "border-transparent hover:scale-105"
              }`}
              style={{ backgroundColor: c.value }}
            />
          ))}
          <input
            type="color"
            value={values.accent_color || "#D4AF37"}
            onChange={(e) => {
              setValues({ ...values, accent_color: e.target.value });
              applyAccentColor(e.target.value);
            }}
            data-testid="accent-custom"
            title="Couleur personnalisée"
            className="h-10 w-14 cursor-pointer rounded-lg border border-white/15 bg-transparent"
          />
        </div>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/30">
          Boutons, étoiles et détails dorés — aperçu en direct
        </p>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {TEXT_FIELDS.map((fd) => (
          <div key={fd.key} className={fd.textarea ? "sm:col-span-2" : ""}>
            <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
              {fd.label}
            </label>
            {fd.textarea ? (
              <textarea
                rows={2}
                value={values[fd.key] || ""}
                onChange={(e) => setValues({ ...values, [fd.key]: e.target.value })}
                data-testid={`text-field-${fd.key}`}
                className={fieldCls + " resize-none"}
              />
            ) : (
              <input
                value={values[fd.key] || ""}
                onChange={(e) => setValues({ ...values, [fd.key]: e.target.value })}
                data-testid={`text-field-${fd.key}`}
                className={fieldCls}
              />
            )}
          </div>
        ))}
      </div>

      <button
        type="submit"
        disabled={saving}
        data-testid="texts-save"
        className="mt-5 w-full rounded-xl bg-gold px-6 py-3.5 text-sm font-semibold text-[#0B132B] transition-colors hover:bg-sand disabled:opacity-60"
      >
        {saving ? "Enregistrement…" : "Enregistrer les textes et le style"}
      </button>
    </form>
  );
}
